import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Sparkles,
  Mail,
  Linkedin,
  X as XIcon,
  Phone,
  ShieldCheck,
  CalendarDays,
} from 'lucide-react';

const Footer = () => {
  const emailRef = useRef(null);
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!emailRef.current?.value) return;

    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/newsletter`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailRef.current.value }),
        }
      );
      alert('Thanks! AI tips coming your way 🚀');
      emailRef.current.value = '';
    } catch (err) {
      alert('Oops! Try again or email us directly.');
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand + Description */}
          <div className="md:col-span-3">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent">
                AI Workshop
              </span>
            </div>
            <p className="text-slate-400 leading-relaxed max-w-md">
              We help small business owners implement AI that actually
              works. Get your custom AI Implementation Blueprint with
              roadmap, tools, prompts, and ROI projections — built on{" "}
              {displayCount} vetted tools and 2026 industry benchmarks.
            </p>

            <div className="mt-8 flex items-center gap-3 bg-blue-900/30 border border-blue-500/30 px-5 py-3 rounded-2xl">
              <ShieldCheck className="w-6 h-6 text-[#3b82f6]" />
              <span className="text-sm font-semibold text-blue-300">
                100% Satisfaction Guarantee
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="text-white font-semibold mb-6">Products</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/"
                  className="hover:text-white transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/audit"
                  className="hover:text-white transition-colors"
                >
                  AI Implementation Blueprint
                </Link>
              </li>
              <li>
                <Link
                  to="/signal-inbox"
                  className="hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  Signal Inbox
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full leading-none">
                    New
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/discover"
                  className="hover:text-white transition-colors"
                >
                  Discover (free)
                </Link>
              </li>
            </ul>
          </div>

          {/* Industries */}
          <div className="md:col-span-2">
            <h4 className="text-white font-semibold mb-6">Industries</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/hvac" className="hover:text-white transition-colors">
                  HVAC &amp; Home Services
                </Link>
              </li>
              <li>
                <Link to="/dental" className="hover:text-white transition-colors">
                  Dental Practices
                </Link>
              </li>
              <li>
                <Link to="/restaurant" className="hover:text-white transition-colors">
                  Restaurants
                </Link>
              </li>
              <li>
                <Link to="/ecommerce" className="hover:text-white transition-colors">
                  Ecommerce &amp; DTC
                </Link>
              </li>
              <li>
                <Link to="/retail" className="hover:text-white transition-colors">
                  Retail (Brick &amp; Mortar)
                </Link>
              </li>
              <li>
                <Link to="/real-estate" className="hover:text-white transition-colors">
                  Real Estate
                </Link>
              </li>
              <li>
                <Link to="/healthcare" className="hover:text-white transition-colors">
                  Healthcare
                </Link>
              </li>
              <li>
                <Link to="/legal" className="hover:text-white transition-colors">
                  Legal
                </Link>
              </li>
              <li>
                <Link to="/accounting" className="hover:text-white transition-colors">
                  Accounting
                </Link>
              </li>
              <li>
                <Link to="/construction" className="hover:text-white transition-colors">
                  Construction
                </Link>
              </li>
              <li>
                <Link to="/auto-repair" className="hover:text-white transition-colors">
                  Auto Repair
                </Link>
              </li>
              <li>
                <Link to="/med-spa" className="hover:text-white transition-colors">
                  Med Spa
                </Link>
              </li>
              <li>
                <Link to="/saas" className="hover:text-white transition-colors">
                  SaaS Companies
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="md:col-span-2">
            <h4 className="text-white font-semibold mb-6">
              Resources
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="/sample-report.pdf"
                  className="hover:text-white transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Sample Blueprint
                </a>
              </li>
              <li>
                <Link
                  to="/discover"
                  className="hover:text-white transition-colors"
                >
                  AI ideas for your industry
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div className="md:col-span-2">
            <h4 className="text-white font-semibold mb-6">
              Get in Touch
            </h4>

            <div className="space-y-4 mb-8">
              <a
                href="tel:+19494146437"
                className="flex items-center gap-3 hover:text-white transition-colors"
              >
                <Phone className="w-5 h-5 text-[#3b82f6]" />
                <span>(949) 414-6437</span>
              </a>
              <a
                href="mailto:aiworkshopllc@gmail.com"
                className="flex items-center gap-3 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-[#3b82f6]" />
                <span>aiworkshopllc@gmail.com</span>
              </a>
              <a
                href="https://cal.com/aiworkshopllc/15min"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-white transition-colors"
              >
                <CalendarDays className="w-5 h-5 text-[#3b82f6]" />
                <span>Book Free Strategy Call</span>
              </a>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-white font-semibold mb-3">
                Weekly AI Tips for SMBs
              </h4>
              <form
                onSubmit={handleNewsletter}
                className="flex gap-2"
              >
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="you@example.com"
                  required
                  className="flex-1 bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-700"
                >
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500">
          <div>
            © {new Date().getFullYear()} AI Workshop LLC. All rights
            reserved.
          </div>
          <div className="flex gap-6 mt-4 md:mt-0">
            <Link
              to="/privacy"
              className="hover:text-slate-300"
            >
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-slate-300">
              Terms
            </Link>
            <span>Powered by Velocity Framework™</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
